import React, { useState, useEffect } from 'react';
import { 
  Users, 
  ShoppingBag, 
  MessageSquare, 
  Settings, 
  ShieldCheck, 
  CheckCircle2, 
  AlertCircle, 
  Search, 
  Filter, 
  ChevronRight, 
  TrendingUp, 
  Trash2, 
  Edit, 
  BarChart4, 
  Loader2, 
  ArrowLeft,
  Zap,
  DollarSign,
  Activity,
  Lightbulb,
  TrendingDown,
  Download,
  Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { format, subDays, startOfMonth, endOfMonth, startOfYear, endOfYear, eachMonthOfInterval, parseISO } from 'date-fns';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ResponsiveContainer, 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  Tooltip, 
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/db/supabase';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'orders' | 'community' | 'users' | 'config' | 'ai-stats'>('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [posts, setPosts] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [config, setConfig] = useState<any>({
    ai_provider: 'wenxin',
    ai_model: 'ernie-4.0-8k',
    ai_api_key: ''
  });
  const [aiStats, setAiStats] = useState<any>(null);
  const [aiLogsDateRange, setAiLogsDateRange] = useState<'today' | 'week' | 'month'>('today');
  const [costAlertRules, setCostAlertRules] = useState<any[]>([]);
  const [editingRule, setEditingRule] = useState<any>(null);
  const [autoOptimizationConfig, setAutoOptimizationConfig] = useState<any>(null);
  const [optimizationReports, setOptimizationReports] = useState<any[]>([]);
  const [exportStartDate, setExportStartDate] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [exportEndDate, setExportEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [exporting, setExporting] = useState(false);
  const [exportType, setExportType] = useState<'daily' | 'monthly' | 'yearly' | 'batch'>('daily');
  const [exportHistory, setExportHistory] = useState<any[]>([]);
  const [showExportHistory, setShowExportHistory] = useState(false);

  const fetchStats = async () => {
    try {
      const { data: orderData } = await supabase.from('orders').select('total_amount, status');
      const { data: userData } = await supabase.from('profiles').select('id, membership_tier');
      const { data: postData } = await supabase.from('community_posts').select('id');

      const totalRevenue = orderData?.filter(o => o.status === 'paid').reduce((acc, curr) => acc + curr.total_amount, 0) || 0;
      const totalUsers = userData?.length || 0;
      const totalPosts = postData?.length || 0;
      const activeOrders = orderData?.filter(o => o.status === 'pending').length || 0;

      setStats({
        totalRevenue,
        totalUsers,
        totalPosts,
        activeOrders,
        revenueData: [
          { name: 'Mon', value: 400 },
          { name: 'Tue', value: 300 },
          { name: 'Wed', value: 500 },
          { name: 'Thu', value: 280 },
          { name: 'Fri', value: 590 },
          { name: 'Sat', value: 800 },
          { name: 'Sun', value: 700 }
        ]
      });
    } catch (err) {
      toast.error('获取统计数据失败');
    }
  };

  const fetchOrders = async () => {
    const { data } = await supabase.from('orders').select('*').order('created_at', { ascending: false });
    setOrders(data || []);
  };

  const fetchPosts = async () => {
    const { data } = await supabase.from('community_posts').select('*, profiles(username)').order('created_at', { ascending: false });
    setPosts(data || []);
  };

  const fetchUsers = async () => {
    const { data } = await supabase.from('profiles').select('*').order('created_at', { ascending: false });
    setUsers(data || []);
  };

  const fetchConfig = async () => {
    const { data } = await supabase.from('system_config').select('*').single();
    if (data) {
      setConfig({
        ai_provider: data.ai_provider || 'wenxin',
        ai_model: data.ai_model || 'ernie-4.0-8k',
        ai_api_key: data.ai_api_key || ''
      });
    }
  };

  const fetchAiStats = async () => {
    try {
      let startDate = new Date();
      if (aiLogsDateRange === 'today') {
        startDate.setHours(0, 0, 0, 0);
      } else if (aiLogsDateRange === 'week') {
        startDate.setDate(startDate.getDate() - 7);
      } else {
        startDate.setDate(startDate.getDate() - 30);
      }

      const { data: logs } = await supabase
        .from('ai_api_logs')
        .select('*')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false });

      if (!logs) return;

      // 按服务商分组统计
      const providerStats: Record<string, any> = {};
      logs.forEach((log: any) => {
        if (!providerStats[log.provider]) {
          providerStats[log.provider] = {
            provider: log.provider,
            calls: 0,
            totalTokens: 0,
            totalCost: 0,
            successRate: 0,
            successCount: 0,
          };
        }
        providerStats[log.provider].calls += 1;
        providerStats[log.provider].totalTokens += log.total_tokens || 0;
        providerStats[log.provider].totalCost += parseFloat(log.cost_usd || 0);
        if (log.status === 'success') providerStats[log.provider].successCount += 1;
      });

      Object.values(providerStats).forEach((stat: any) => {
        stat.successRate = stat.calls > 0 ? ((stat.successCount / stat.calls) * 100).toFixed(1) : 0;
      });

      // 按日期分组（用于趋势图）
      const dailyStats: Record<string, any> = {};
      logs.forEach((log: any) => {
        const date = new Date(log.created_at).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
        if (!dailyStats[date]) {
          dailyStats[date] = { date, calls: 0, cost: 0, tokens: 0 };
        }
        dailyStats[date].calls += 1;
        dailyStats[date].cost += parseFloat(log.cost_usd || 0);
        dailyStats[date].tokens += log.total_tokens || 0;
      });

      const totalCalls = logs.length;
      const totalCost = logs.reduce((sum: number, log: any) => sum + parseFloat(log.cost_usd || 0), 0);
      const totalTokens = logs.reduce((sum: number, log: any) => sum + (log.total_tokens || 0), 0);

      setAiStats({
        totalCalls,
        totalCost: totalCost.toFixed(4),
        totalTokens,
        providerStats: Object.values(providerStats),
        dailyTrend: Object.values(dailyStats).slice(-14), // 最近14天
      });

      // 检查预警规则
      await checkCostAlerts(totalCost, providerStats);
    } catch (err) {
      toast.error('获取 AI 统计数据失败');
    }
  };

  const fetchCostAlertRules = async () => {
    const { data } = await supabase.from('cost_alert_rules').select('*').order('created_at');
    setCostAlertRules(data || []);
  };

  const fetchAutoOptimizationConfig = async () => {
    const { data } = await supabase.from('auto_optimization_config').select('*').single();
    setAutoOptimizationConfig(data);
  };

  const fetchOptimizationReports = async () => {
    const { data } = await supabase
      .from('optimization_reports')
      .select('*')
      .order('report_date', { ascending: false })
      .limit(7);
    setOptimizationReports(data || []);
  };

  const fetchExportHistory = async () => {
    const { data } = await supabase
      .from('export_history')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(10);
    setExportHistory(data || []);
  };

  const generateMonthlyReport = (reports: any[]) => {
    const monthlyData: Record<string, any> = {};
    
    reports.forEach((report: any) => {
      const month = format(new Date(report.report_date), 'yyyy-MM');
      if (!monthlyData[month]) {
        monthlyData[month] = {
          month,
          total_calls: 0,
          actual_cost: 0,
          estimated_cost: 0,
          saved_amount: 0,
          report_count: 0,
        };
      }
      monthlyData[month].total_calls += report.total_calls || 0;
      monthlyData[month].actual_cost += report.actual_cost || 0;
      monthlyData[month].estimated_cost += report.estimated_cost || 0;
      monthlyData[month].saved_amount += report.saved_amount || 0;
      monthlyData[month].report_count += 1;
    });

    return Object.values(monthlyData).map((data: any) => ({
      ...data,
      saved_percentage: data.estimated_cost > 0 ? data.saved_amount / data.estimated_cost : 0,
      avg_success_rate: 0.95, // 简化处理
    }));
  };

  const generateYearlyReport = (reports: any[]) => {
    const yearlyData: Record<string, any> = {};
    
    reports.forEach((report: any) => {
      const year = format(new Date(report.report_date), 'yyyy');
      if (!yearlyData[year]) {
        yearlyData[year] = {
          year,
          total_calls: 0,
          actual_cost: 0,
          estimated_cost: 0,
          saved_amount: 0,
          report_count: 0,
        };
      }
      yearlyData[year].total_calls += report.total_calls || 0;
      yearlyData[year].actual_cost += report.actual_cost || 0;
      yearlyData[year].estimated_cost += report.estimated_cost || 0;
      yearlyData[year].saved_amount += report.saved_amount || 0;
      yearlyData[year].report_count += 1;
    });

    return Object.values(yearlyData).map((data: any) => ({
      ...data,
      saved_percentage: data.estimated_cost > 0 ? data.saved_amount / data.estimated_cost : 0,
      avg_success_rate: 0.95, // 简化处理
    }));
  };

  const handleExportReports = async () => {
    setExporting(true);
    try {
      // 获取日期范围内的报告
      const { data: reports } = await supabase
        .from('optimization_reports')
        .select('*')
        .gte('report_date', exportStartDate)
        .lte('report_date', exportEndDate)
        .order('report_date', { ascending: true });

      if (!reports || reports.length === 0) {
        toast.error('所选日期范围内没有报告数据');
        return;
      }

      let fileName = '';
      let workbook: XLSX.WorkBook;

      if (exportType === 'daily') {
        // 日报告导出
        workbook = generateDailyReportWorkbook(reports);
        fileName = `AI优化日报告_${exportStartDate}_${exportEndDate}.xlsx`;
      } else if (exportType === 'monthly') {
        // 月度汇总导出
        const monthlyData = generateMonthlyReport(reports);
        workbook = generateMonthlyReportWorkbook(monthlyData, reports);
        fileName = `AI优化月度汇总_${exportStartDate}_${exportEndDate}.xlsx`;
      } else if (exportType === 'yearly') {
        // 年度汇总导出
        const yearlyData = generateYearlyReport(reports);
        workbook = generateYearlyReportWorkbook(yearlyData, reports);
        fileName = `AI优化年度汇总_${exportStartDate}_${exportEndDate}.xlsx`;
      } else {
        // 批量导出（按月份分别导出）
        await handleBatchExport(reports);
        return;
      }

      // 导出文件
      XLSX.writeFile(workbook, fileName);

      // 记录导出历史
      await supabase.from('export_history').insert({
        user_id: user?.id,
        export_type: exportType,
        start_date: exportStartDate,
        end_date: exportEndDate,
        file_name: fileName,
        report_count: reports.length,
      });

      toast.success('报告导出成功');
      fetchExportHistory();
    } catch (error: any) {
      console.error('导出失败:', error);
      toast.error(error.message || '导出失败');
    } finally {
      setExporting(false);
    }
  };

  const generateDailyReportWorkbook = (reports: any[]) => {
    const workbook = XLSX.utils.book_new();

    // 工作表1: 成本对比表
    const costComparisonData = reports.map((report: any) => ({
      '日期': format(new Date(report.report_date), 'yyyy-MM-dd'),
      '总调用次数': report.total_calls || 0,
      '实际成本 ($)': (report.actual_cost || 0).toFixed(4),
      '预估成本 ($)': (report.estimated_cost || 0).toFixed(4),
      '节省金额 ($)': (report.saved_amount || 0).toFixed(4),
      '节省比例 (%)': ((report.saved_percentage || 0) * 100).toFixed(2),
      '平均成功率 (%)': ((report.avg_success_rate || 0) * 100).toFixed(2),
    }));
    const ws1 = XLSX.utils.json_to_sheet(costComparisonData);
    XLSX.utils.book_append_sheet(workbook, ws1, '成本对比');

    // 工作表2: 服务商切换记录
    const providerSwitchData = reports.map((report: any) => ({
      '日期': format(new Date(report.report_date), 'yyyy-MM-dd'),
      '使用服务商': report.provider_used || 'N/A',
      '推荐服务商': report.recommended_provider || 'N/A',
      '切换次数': report.switch_count || 0,
      '切换原因': report.switch_count > 0 ? '成本优化' : '无切换',
    }));
    const ws2 = XLSX.utils.json_to_sheet(providerSwitchData);
    XLSX.utils.book_append_sheet(workbook, ws2, '服务商切换');

    // 工作表3: 节省金额统计
    const totalSaved = reports.reduce((sum: number, r: any) => sum + (r.saved_amount || 0), 0);
    const totalCalls = reports.reduce((sum: number, r: any) => sum + (r.total_calls || 0), 0);
    const avgSavedPercentage = reports.reduce((sum: number, r: any) => sum + (r.saved_percentage || 0), 0) / reports.length;
    
    const summaryData = [
      { '统计项': '报告日期范围', '数值': `${exportStartDate} 至 ${exportEndDate}` },
      { '统计项': '报告天数', '数值': reports.length },
      { '统计项': '总调用次数', '数值': totalCalls },
      { '统计项': '累计节省金额 ($)', '数值': totalSaved.toFixed(4) },
      { '统计项': '平均节省比例 (%)', '数值': (avgSavedPercentage * 100).toFixed(2) },
      { '统计项': '最高单日节省 ($)', '数值': Math.max(...reports.map((r: any) => r.saved_amount || 0)).toFixed(4) },
      { '统计项': '最低单日节省 ($)', '数值': Math.min(...reports.map((r: any) => r.saved_amount || 0)).toFixed(4) },
    ];
    const ws3 = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, ws3, '节省统计');

    // 工作表4: 图表数据（用于可视化）
    const chartData = reports.map((report: any) => ({
      '日期': format(new Date(report.report_date), 'yyyy-MM-dd'),
      '实际成本': (report.actual_cost || 0).toFixed(4),
      '预估成本': (report.estimated_cost || 0).toFixed(4),
      '节省金额': (report.saved_amount || 0).toFixed(4),
    }));
    const ws4 = XLSX.utils.json_to_sheet(chartData);
    XLSX.utils.book_append_sheet(workbook, ws4, '图表数据');

    return workbook;
  };

  const generateMonthlyReportWorkbook = (monthlyData: any[], reports: any[]) => {
    const workbook = XLSX.utils.book_new();

    // 工作表1: 月度汇总
    const monthlySummary = monthlyData.map((data: any) => ({
      '月份': data.month,
      '报告天数': data.report_count,
      '总调用次数': data.total_calls,
      '实际成本 ($)': data.actual_cost.toFixed(4),
      '预估成本 ($)': data.estimated_cost.toFixed(4),
      '节省金额 ($)': data.saved_amount.toFixed(4),
      '节省比例 (%)': (data.saved_percentage * 100).toFixed(2),
    }));
    const ws1 = XLSX.utils.json_to_sheet(monthlySummary);
    XLSX.utils.book_append_sheet(workbook, ws1, '月度汇总');

    // 工作表2: 详细日报告
    const dailyDetails = reports.map((report: any) => ({
      '日期': format(new Date(report.report_date), 'yyyy-MM-dd'),
      '调用次数': report.total_calls || 0,
      '实际成本 ($)': (report.actual_cost || 0).toFixed(4),
      '节省金额 ($)': (report.saved_amount || 0).toFixed(4),
    }));
    const ws2 = XLSX.utils.json_to_sheet(dailyDetails);
    XLSX.utils.book_append_sheet(workbook, ws2, '详细日报告');

    return workbook;
  };

  const generateYearlyReportWorkbook = (yearlyData: any[], reports: any[]) => {
    const workbook = XLSX.utils.book_new();

    // 工作表1: 年度汇总
    const yearlySummary = yearlyData.map((data: any) => ({
      '年份': data.year,
      '报告天数': data.report_count,
      '总调用次数': data.total_calls,
      '实际成本 ($)': data.actual_cost.toFixed(4),
      '预估成本 ($)': data.estimated_cost.toFixed(4),
      '节省金额 ($)': data.saved_amount.toFixed(4),
      '节省比例 (%)': (data.saved_percentage * 100).toFixed(2),
    }));
    const ws1 = XLSX.utils.json_to_sheet(yearlySummary);
    XLSX.utils.book_append_sheet(workbook, ws1, '年度汇总');

    // 工作表2: 月度趋势
    const monthlyData = generateMonthlyReport(reports);
    const monthlyTrend = monthlyData.map((data: any) => ({
      '月份': data.month,
      '调用次数': data.total_calls,
      '节省金额 ($)': data.saved_amount.toFixed(4),
    }));
    const ws2 = XLSX.utils.json_to_sheet(monthlyTrend);
    XLSX.utils.book_append_sheet(workbook, ws2, '月度趋势');

    return workbook;
  };

  const handleBatchExport = async (reports: any[]) => {
    try {
      const zip = new JSZip();
      
      // 按月份分组
      const monthlyGroups: Record<string, any[]> = {};
      reports.forEach((report: any) => {
        const month = format(new Date(report.report_date), 'yyyy-MM');
        if (!monthlyGroups[month]) {
          monthlyGroups[month] = [];
        }
        monthlyGroups[month].push(report);
      });

      // 为每个月份生成 Excel 文件
      for (const [month, monthReports] of Object.entries(monthlyGroups)) {
        const workbook = generateDailyReportWorkbook(monthReports);
        const excelBuffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
        zip.file(`AI优化报告_${month}.xlsx`, excelBuffer);
      }

      // 生成压缩包
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AI优化报告批量导出_${exportStartDate}_${exportEndDate}.zip`;
      a.click();
      URL.revokeObjectURL(url);

      // 记录导出历史
      await supabase.from('export_history').insert({
        user_id: user?.id,
        export_type: 'batch',
        start_date: exportStartDate,
        end_date: exportEndDate,
        file_name: `AI优化报告批量导出_${exportStartDate}_${exportEndDate}.zip`,
        report_count: reports.length,
      });

      toast.success('批量导出成功');
      fetchExportHistory();
    } catch (error: any) {
      console.error('批量导出失败:', error);
      toast.error(error.message || '批量导出失败');
    }
  };

  const handleDeleteExportHistory = async (id: string) => {
    const { error } = await supabase.from('export_history').delete().eq('id', id);
    if (error) {
      toast.error('删除失败');
    } else {
      toast.success('已删除导出记录');
      fetchExportHistory();
    }
  };

  const handleUpdateAutoOptimization = async (updates: any) => {
    const { error } = await supabase
      .from('auto_optimization_config')
      .update(updates)
      .eq('id', autoOptimizationConfig.id);
    
    if (error) {
      toast.error('更新失败');
    } else {
      toast.success('自动优化配置已更新');
      fetchAutoOptimizationConfig();
    }
  };

  const checkCostAlerts = async (totalCost: number, providerStats: Record<string, any>) => {
    const rules = costAlertRules.filter(r => r.is_enabled);
    
    for (const rule of rules) {
      const cost = rule.provider ? providerStats[rule.provider]?.totalCost || 0 : totalCost;
      
      if (cost >= rule.threshold_usd) {
        // 检查是否今天已触发过
        const lastTriggered = rule.last_triggered_at ? new Date(rule.last_triggered_at) : null;
        const today = new Date().toDateString();
        
        if (!lastTriggered || lastTriggered.toDateString() !== today) {
          // 发送预警通知
          toast.error(
            `AI 成本预警：${rule.provider || '全局'}${rule.alert_type === 'daily' ? '今日' : '本月'}成本已达 $${cost.toFixed(2)}，超过阈值 $${rule.threshold_usd}`,
            { duration: 10000 }
          );
          
          // 更新触发时间
          await supabase
            .from('cost_alert_rules')
            .update({ last_triggered_at: new Date().toISOString() })
            .eq('id', rule.id);
        }
      }
    }
  };

  const handleUpdateAlertRule = async (ruleId: string, updates: any) => {
    const { error } = await supabase
      .from('cost_alert_rules')
      .update(updates)
      .eq('id', ruleId);
    
    if (error) {
      toast.error('更新失败');
    } else {
      toast.success('预警规则已更新');
      fetchCostAlertRules();
    }
  };

  const handleUpdateRole = async (userId: string, newRole: string) => {
    const { error } = await supabase.from('profiles').update({ role: newRole }).eq('id', userId);
    if (error) toast.error('修改权限失败');
    else {
      toast.success('修改权限成功');
      fetchUsers();
    }
  };

  const renderUsers = () => (
    <Card className="rounded-2xl border-border/50 overflow-hidden animate-fade-in">
      <CardHeader className="p-6 border-b border-border/50 bg-accent-paid/5">
        <CardTitle className="text-lg font-bold text-accent-paid">用户管理</CardTitle>
        <CardDescription>管理用户角色权限，支持降级或升级管理员</CardDescription>
      </CardHeader>
      <Table>
        <TableHeader className="bg-muted/50">
          <TableRow>
            <TableHead className="font-bold">用户</TableHead>
            <TableHead className="font-bold">邮箱/手机</TableHead>
            <TableHead className="font-bold text-center">当前角色</TableHead>
            <TableHead className="font-bold text-center">注册时间</TableHead>
            <TableHead className="font-bold text-right">权限操作</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u.id}>
              <TableCell className="font-bold">{u.username || '新用户'}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{u.email || u.phone}</TableCell>
              <TableCell className="text-center">
                <Badge variant={u.role === 'admin' ? 'default' : 'secondary'} className={u.role === 'admin' ? 'bg-primary' : ''}>
                  {u.role === 'admin' ? '管理员' : '普通用户'}
                </Badge>
              </TableCell>
              <TableCell className="text-center text-xs text-muted-foreground">
                {new Date(u.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 gap-1">
                      <Settings className="h-3.5 w-3.5" /> 更改角色
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleUpdateRole(u.id, 'user')}>设为普通用户</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleUpdateRole(u.id, 'admin')} className="text-primary font-bold">设为管理员</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );

  const handleSaveConfig = async () => {
    const { error } = await supabase.from('system_config').upsert({
      id: 1, // 假设只有一条配置记录
      ...config,
      updated_at: new Date().toISOString()
    });
    if (error) toast.error('保存失败');
    else toast.success('系统配置已实时生效');
  };

  const renderAiStats = () => {
    if (!aiStats) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

    const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

    return (
      <div className="space-y-6 animate-fade-in">
        {/* 顶部统计卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="rounded-2xl border-border/50 bg-gradient-to-br from-blue-500/5 to-blue-500/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Activity className="h-4 w-4" /> 总调用次数
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{aiStats.totalCalls.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground mt-1">过去 {aiLogsDateRange === 'today' ? '24小时' : aiLogsDateRange === 'week' ? '7天' : '30天'}</p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/50 bg-gradient-to-br from-green-500/5 to-green-500/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Zap className="h-4 w-4" /> Token 消耗
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{(aiStats.totalTokens / 1000).toFixed(1)}K</div>
              <p className="text-xs text-muted-foreground mt-1">约 {aiStats.totalCalls > 0 ? Math.floor(aiStats.totalTokens / aiStats.totalCalls) : 0} tokens/次</p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/50 bg-gradient-to-br from-amber-500/5 to-amber-500/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <DollarSign className="h-4 w-4" /> 总成本
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-600">${aiStats.totalCost}</div>
              <p className="text-xs text-muted-foreground mt-1">约 ¥{(parseFloat(aiStats.totalCost) * 7.2).toFixed(2)} CNY</p>
            </CardContent>
          </Card>
        </div>

        {/* 日期范围选择 */}
        <div className="flex justify-end">
          <Tabs value={aiLogsDateRange} onValueChange={(val: any) => setAiLogsDateRange(val)}>
            <TabsList>
              <TabsTrigger value="today">今日</TabsTrigger>
              <TabsTrigger value="week">近7天</TabsTrigger>
              <TabsTrigger value="month">近30天</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* 趋势图 */}
        <Card className="rounded-2xl border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" /> 调用趋势
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={aiStats.dailyTrend}>
                <defs>
                  <linearGradient id="colorCalls" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }} 
                />
                <Area type="monotone" dataKey="calls" stroke="#3b82f6" fillOpacity={1} fill="url(#colorCalls)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* 服务商对比 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="rounded-2xl border-border/50">
            <CardHeader>
              <CardTitle className="text-lg font-bold">服务商调用分布</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={aiStats.providerStats}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.provider}: ${entry.calls}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="calls"
                  >
                    {aiStats.providerStats.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/50">
            <CardHeader>
              <CardTitle className="text-lg font-bold">服务商成本对比</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={aiStats.providerStats}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="provider" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }} 
                  />
                  <Bar dataKey="totalCost" fill="#f59e0b" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* 详细统计表格 */}
        <Card className="rounded-2xl border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-bold">服务商详细统计</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>服务商</TableHead>
                  <TableHead>调用次数</TableHead>
                  <TableHead>Token 消耗</TableHead>
                  <TableHead>总成本 (USD)</TableHead>
                  <TableHead>成功率</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {aiStats.providerStats.map((stat: any) => (
                  <TableRow key={stat.provider}>
                    <TableCell className="font-medium">
                      <Badge variant="outline">{stat.provider}</Badge>
                    </TableCell>
                    <TableCell>{stat.calls.toLocaleString()}</TableCell>
                    <TableCell>{(stat.totalTokens / 1000).toFixed(1)}K</TableCell>
                    <TableCell className="font-bold text-amber-600">${stat.totalCost.toFixed(4)}</TableCell>
                    <TableCell>
                      <Badge variant={parseFloat(stat.successRate) > 95 ? 'default' : 'destructive'}>
                        {stat.successRate}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* 成本预警规则 */}
        <Card className="rounded-2xl border-border/50 mt-6">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" /> 成本预警规则
            </CardTitle>
            <CardDescription>设置每日/每月成本阈值，超过后自动发送通知</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>服务商</TableHead>
                  <TableHead>类型</TableHead>
                  <TableHead>阈值 (USD)</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>上次触发</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {costAlertRules.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell>
                      <Badge variant="outline">{rule.provider || '全局'}</Badge>
                    </TableCell>
                    <TableCell>{rule.alert_type === 'daily' ? '每日' : '每月'}</TableCell>
                    <TableCell>
                      {editingRule?.id === rule.id ? (
                        <Input
                          type="number"
                          value={editingRule.threshold_usd}
                          onChange={(e) => setEditingRule({ ...editingRule, threshold_usd: parseFloat(e.target.value) })}
                          className="w-24 h-8"
                        />
                      ) : (
                        <span className="font-bold">${rule.threshold_usd}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={rule.is_enabled ? 'default' : 'secondary'}>
                        {rule.is_enabled ? '启用' : '禁用'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {rule.last_triggered_at ? new Date(rule.last_triggered_at).toLocaleString('zh-CN') : '未触发'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {editingRule?.id === rule.id ? (
                          <>
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => {
                                handleUpdateAlertRule(rule.id, { threshold_usd: editingRule.threshold_usd });
                                setEditingRule(null);
                              }}
                            >
                              保存
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingRule(null)}>
                              取消
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="outline" onClick={() => setEditingRule(rule)}>
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant={rule.is_enabled ? 'destructive' : 'default'}
                              onClick={() => handleUpdateAlertRule(rule.id, { is_enabled: !rule.is_enabled })}
                            >
                              {rule.is_enabled ? '禁用' : '启用'}
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* AI 成本优化建议 */}
        {aiStats && (
          <Card className="rounded-2xl border-border/50 mt-6 bg-gradient-to-br from-blue-500/5 to-blue-500/10">
            <CardHeader>
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-blue-500" /> 智能优化建议
              </CardTitle>
              <CardDescription>基于历史数据分析的成本优化方案</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* 成本效益比分析 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {aiStats.providerStats.map((stat: any) => {
                  const costPerCall = stat.totalCost / stat.calls;
                  const costPer1kTokens = (stat.totalCost / stat.totalTokens) * 1000;
                  
                  return (
                    <div key={stat.provider} className="p-4 bg-card border border-border/50 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline">{stat.provider}</Badge>
                        <Badge variant={parseFloat(stat.successRate) > 95 ? 'default' : 'secondary'}>
                          {stat.successRate}% 成功率
                        </Badge>
                      </div>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">单次调用成本</span>
                          <span className="font-bold">${costPerCall.toFixed(4)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">1K Token 成本</span>
                          <span className="font-bold">${costPer1kTokens.toFixed(4)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 优化建议列表 */}
              <div className="space-y-3">
                {aiStats.providerStats.length > 1 && (() => {
                  const cheapest = aiStats.providerStats.reduce((min: any, stat: any) => {
                    const costPer1k = (stat.totalCost / stat.totalTokens) * 1000;
                    const minCostPer1k = (min.totalCost / min.totalTokens) * 1000;
                    return costPer1k < minCostPer1k ? stat : min;
                  });
                  
                  const mostExpensive = aiStats.providerStats.reduce((max: any, stat: any) => {
                    const costPer1k = (stat.totalCost / stat.totalTokens) * 1000;
                    const maxCostPer1k = (max.totalCost / max.totalTokens) * 1000;
                    return costPer1k > maxCostPer1k ? stat : max;
                  });

                  const cheapestCost = (cheapest.totalCost / cheapest.totalTokens) * 1000;
                  const expensiveCost = (mostExpensive.totalCost / mostExpensive.totalTokens) * 1000;
                  const savingsPercent = ((expensiveCost - cheapestCost) / expensiveCost) * 100;

                  return cheapest.provider !== mostExpensive.provider && (
                    <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                          <TrendingDown className="h-5 w-5 text-green-500" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-green-700 dark:text-green-400 mb-1">
                            推荐切换到 {cheapest.provider}
                          </h4>
                          <p className="text-sm text-muted-foreground mb-2">
                            {cheapest.provider} 的 Token 成本比 {mostExpensive.provider} 低 {savingsPercent.toFixed(1)}%，
                            预计可节省约 ${((mostExpensive.totalCost - cheapest.totalCost) * (aiStats.totalCalls / mostExpensive.calls)).toFixed(2)} USD
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Badge variant="outline" className="text-xs">
                              {cheapest.provider}: ${cheapestCost.toFixed(4)}/1K
                            </Badge>
                            <span>vs</span>
                            <Badge variant="outline" className="text-xs">
                              {mostExpensive.provider}: ${expensiveCost.toFixed(4)}/1K
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* 下月成本预测 */}
                {aiStats.dailyTrend.length >= 7 && (() => {
                  const recentDays = aiStats.dailyTrend.slice(-7);
                  const avgDailyCost = recentDays.reduce((sum: number, day: any) => sum + day.cost, 0) / recentDays.length;
                  const predictedMonthlyCost = avgDailyCost * 30;
                  const currentMonthlyCost = parseFloat(aiStats.totalCost);
                  const trend = predictedMonthlyCost > currentMonthlyCost ? 'up' : 'down';

                  return (
                    <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                          <TrendingUp className="h-5 w-5 text-blue-500" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-blue-700 dark:text-blue-400 mb-1">
                            下月成本预测
                          </h4>
                          <p className="text-sm text-muted-foreground mb-2">
                            基于近 7 天平均每日成本 ${avgDailyCost.toFixed(4)}，预计下月总成本约 ${predictedMonthlyCost.toFixed(2)} USD
                            {trend === 'up' ? '（呈上升趋势 📈）' : '（呈下降趋势 📉）'}
                          </p>
                          <div className="flex items-center gap-2 text-xs">
                            <Badge variant={trend === 'up' ? 'destructive' : 'default'}>
                              {trend === 'up' ? '成本上升' : '成本下降'}
                            </Badge>
                            <span className="text-muted-foreground">
                              建议设置月度预警阈值: ${(predictedMonthlyCost * 1.2).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* 使用优化建议 */}
                {aiStats.totalCalls > 100 && (
                  <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                        <Lightbulb className="h-5 w-5 text-amber-500" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-amber-700 dark:text-amber-400 mb-1">
                          使用优化建议
                        </h4>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          <li>• 对于简单任务，优先使用成本较低的模型（如通义千问）</li>
                          <li>• 启用响应缓存，减少重复调用</li>
                          <li>• 优化 Prompt 长度，减少不必要的 Token 消耗</li>
                          <li>• 设置合理的 max_tokens 限制，避免过长响应</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* 自动优化配置 */}
        {autoOptimizationConfig && (
          <Card className="rounded-2xl border-border/50 mt-6 bg-gradient-to-br from-purple-500/5 to-purple-500/10">
            <CardHeader>
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <Zap className="h-5 w-5 text-purple-500" /> AI 自动优化配置
              </CardTitle>
              <CardDescription>启用后系统将自动选择最优服务商，降低成本</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-card border border-border/50 rounded-xl">
                <div>
                  <p className="font-bold">自动优化状态</p>
                  <p className="text-sm text-muted-foreground">
                    {autoOptimizationConfig.is_enabled ? '已启用，系统将自动选择最优服务商' : '已禁用，使用手动配置的服务商'}
                  </p>
                </div>
                <Button
                  variant={autoOptimizationConfig.is_enabled ? 'destructive' : 'default'}
                  onClick={() => handleUpdateAutoOptimization({ is_enabled: !autoOptimizationConfig.is_enabled })}
                >
                  {autoOptimizationConfig.is_enabled ? '禁用' : '启用'}
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-card border border-border/50 rounded-xl">
                  <p className="text-sm text-muted-foreground mb-2">优化策略</p>
                  <Select
                    value={autoOptimizationConfig.strategy}
                    onValueChange={(val) => handleUpdateAutoOptimization({ strategy: val })}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cost_priority">成本优先</SelectItem>
                      <SelectItem value="quality_priority">质量优先</SelectItem>
                      <SelectItem value="balanced">平衡模式</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="p-4 bg-card border border-border/50 rounded-xl">
                  <p className="text-sm text-muted-foreground mb-2">最低成功率</p>
                  <Input
                    type="number"
                    value={autoOptimizationConfig.min_success_rate}
                    onChange={(e) => handleUpdateAutoOptimization({ min_success_rate: parseFloat(e.target.value) })}
                    min={80}
                    max={100}
                    step={0.1}
                    className="rounded-xl"
                  />
                </div>

                <div className="p-4 bg-card border border-border/50 rounded-xl">
                  <p className="text-sm text-muted-foreground mb-2">当前服务商</p>
                  <Badge variant="outline" className="text-base px-3 py-1">
                    {autoOptimizationConfig.current_provider}
                  </Badge>
                </div>
              </div>

              {/* 优化报告 */}
              {optimizationReports.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-bold flex items-center gap-2">
                      <BarChart4 className="h-4 w-4" /> 近 7 日优化报告
                    </h4>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 px-3 rounded-lg"
                      onClick={() => {
                        const exportSection = document.getElementById('export-section');
                        if (exportSection) {
                          exportSection.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                    >
                      <Download className="h-3 w-3 mr-1" /> 导出报告
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {optimizationReports.map((report) => (
                      <div key={report.id} className="p-3 bg-card border border-border/50 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">
                            {new Date(report.report_date).toLocaleDateString('zh-CN')}
                          </span>
                          <Badge variant={report.savings_usd > 0 ? 'default' : 'secondary'}>
                            {report.savings_usd > 0 ? `节省 $${report.savings_usd.toFixed(4)}` : '无节省'}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-xs text-muted-foreground">
                          <div>
                            <p>调用次数</p>
                            <p className="font-bold text-foreground">{report.total_calls}</p>
                          </div>
                          <div>
                            <p>实际成本</p>
                            <p className="font-bold text-foreground">${report.total_cost_usd.toFixed(4)}</p>
                          </div>
                          <div>
                            <p>节省比例</p>
                            <p className="font-bold text-green-600">{report.savings_percent.toFixed(1)}%</p>
                          </div>
                          <div>
                            <p>使用服务商</p>
                            <p className="font-bold text-foreground">{report.provider_used}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 报告导出区域 */}
              <div id="export-section" className="space-y-3 pt-4 border-t border-border/50">
                <h4 className="font-bold flex items-center gap-2">
                  <Download className="h-4 w-4" /> 导出优化报告
                </h4>
                
                {/* 导出类型选择 */}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground">导出类型</label>
                  <div className="grid grid-cols-4 gap-2">
                    <Button
                      size="sm"
                      variant={exportType === 'daily' ? 'default' : 'outline'}
                      className="h-9"
                      onClick={() => setExportType('daily')}
                    >
                      日报告
                    </Button>
                    <Button
                      size="sm"
                      variant={exportType === 'monthly' ? 'default' : 'outline'}
                      className="h-9"
                      onClick={() => setExportType('monthly')}
                    >
                      月汇总
                    </Button>
                    <Button
                      size="sm"
                      variant={exportType === 'yearly' ? 'default' : 'outline'}
                      className="h-9"
                      onClick={() => setExportType('yearly')}
                    >
                      年汇总
                    </Button>
                    <Button
                      size="sm"
                      variant={exportType === 'batch' ? 'default' : 'outline'}
                      className="h-9"
                      onClick={() => setExportType('batch')}
                    >
                      批量导出
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> 开始日期
                    </label>
                    <Input
                      type="date"
                      value={exportStartDate}
                      onChange={(e) => setExportStartDate(e.target.value)}
                      className="rounded-lg h-9"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> 结束日期
                    </label>
                    <Input
                      type="date"
                      value={exportEndDate}
                      onChange={(e) => setExportEndDate(e.target.value)}
                      className="rounded-lg h-9"
                    />
                  </div>
                </div>
                <Button
                  className="w-full rounded-lg h-10 font-bold"
                  onClick={handleExportReports}
                  disabled={exporting}
                >
                  {exporting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" /> 导出中...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" /> 
                      {exportType === 'batch' ? '批量导出为压缩包' : '导出为 Excel 文件'}
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground">
                  {exportType === 'daily' && '导出文件包含：成本对比表、服务商切换记录、节省金额统计、图表数据'}
                  {exportType === 'monthly' && '导出文件包含：月度汇总表、详细日报告'}
                  {exportType === 'yearly' && '导出文件包含：年度汇总表、月度趋势'}
                  {exportType === 'batch' && '按月份分别生成 Excel 文件并打包为 ZIP 压缩包'}
                </p>

                {/* 导出历史记录 */}
                <div className="pt-3 border-t border-border/50">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="text-sm font-bold">导出历史</h5>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2"
                      onClick={() => setShowExportHistory(!showExportHistory)}
                    >
                      {showExportHistory ? '收起' : '展开'}
                    </Button>
                  </div>
                  {showExportHistory && (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {exportHistory.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-4">暂无导出记录</p>
                      ) : (
                        exportHistory.map((record) => (
                          <div key={record.id} className="p-2 bg-muted/50 rounded-lg text-xs">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium">{record.file_name}</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0"
                                onClick={() => handleDeleteExportHistory(record.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Badge variant="outline" className="text-xs">
                                {record.export_type === 'daily' && '日报告'}
                                {record.export_type === 'monthly' && '月汇总'}
                                {record.export_type === 'yearly' && '年汇总'}
                                {record.export_type === 'batch' && '批量导出'}
                              </Badge>
                              <span>{record.start_date} ~ {record.end_date}</span>
                              <span>·</span>
                              <span>{record.report_count} 条记录</span>
                              <span>·</span>
                              <span>{new Date(record.created_at).toLocaleString('zh-CN')}</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderConfig = () => (
    <div className="space-y-6 animate-fade-in">
      <Card className="rounded-2xl border-border/50">
        <CardHeader className="p-6 border-b border-border/50 bg-success/5">
          <CardTitle className="text-lg font-bold text-success flex items-center gap-2">
            <Settings className="h-5 w-5" /> 系统参数配置
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h4 className="text-sm font-bold flex items-center gap-2">AI 服务商设置 <Badge variant="outline">核心</Badge></h4>
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">主服务商切换</label>
                <Select 
                  value={config.ai_provider} 
                  onValueChange={(val) => setConfig({ ...config, ai_provider: val })}
                >
                  <SelectTrigger className="w-full rounded-lg h-10">
                    <SelectValue placeholder="选择 AI 服务商" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wenxin">百度文心一言 (推荐)</SelectItem>
                    <SelectItem value="openai">OpenAI GPT-4</SelectItem>
                    <SelectItem value="qwen">阿里通义千问</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">模型版本</label>
                <Input 
                  value={config.ai_model} 
                  onChange={(e) => setConfig({ ...config, ai_model: e.target.value })}
                  placeholder="如: ernie-4.0-8k" 
                  className="rounded-lg h-10" 
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">API Key (配置后自动切换)</label>
                <Input 
                  type="password" 
                  value={config.ai_api_key} 
                  onChange={(e) => setConfig({ ...config, ai_api_key: e.target.value })}
                  placeholder="请输入对应的 API Key" 
                  className="rounded-lg h-10" 
                />
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="text-sm font-bold flex items-center gap-2">支付接口设置 <Badge variant="outline">安全</Badge></h4>
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">商户号 (MCH_ID)</label>
                <Input type="password" value="********" className="rounded-lg h-10" disabled />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">微信支付 API V3 密钥</label>
                <Input type="password" value="********" className="rounded-lg h-10" disabled />
              </div>
            </div>
          </div>
          <div className="pt-6 border-t border-border/50 flex justify-end">
            <Button 
              className="bg-primary text-white font-bold h-10 px-8 shadow-glow-sm"
              onClick={handleSaveConfig}
            >
              保存全局配置并生效
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([
        fetchStats(), 
        fetchOrders(), 
        fetchPosts(), 
        fetchUsers(), 
        fetchConfig(), 
        fetchAiStats(), 
        fetchCostAlertRules(),
        fetchAutoOptimizationConfig(),
        fetchOptimizationReports(),
        fetchExportHistory()
      ]);
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    if (activeTab === 'ai-stats') {
      fetchAiStats();
    }
  }, [aiLogsDateRange, activeTab]);

  const handleDeletePost = async (id: string) => {
    if (!confirm('确定删除该帖子吗？')) return;
    const { error } = await supabase.from('community_posts').delete().eq('id', id);
    if (error) toast.error('删除失败');
    else {
      toast.success('删除成功');
      fetchPosts();
    }
  };

  const renderOverview = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: '总营收', value: `¥${stats?.totalRevenue.toFixed(2)}`, icon: <ShoppingBag className="h-6 w-6" />, color: 'bg-primary/10 text-primary' },
          { title: '注册用户', value: stats?.totalUsers, icon: <Users className="h-6 w-6" />, color: 'bg-secondary/10 text-secondary' },
          { title: '活跃帖子', value: stats?.totalPosts, icon: <MessageSquare className="h-6 w-6" />, color: 'bg-accent-paid/10 text-accent-paid' },
          { title: '待处理订单', value: stats?.activeOrders, icon: <ShieldCheck className="h-6 w-6" />, color: 'bg-success/10 text-success' }
        ].map((item, i) => (
          <Card key={i} className="rounded-2xl border-border/50 shadow-sm hover:shadow-md transition-all">
            <CardHeader className="flex flex-row items-center justify-between p-6">
              <div className="space-y-1">
                <CardDescription className="text-xs font-bold uppercase tracking-wider">{item.title}</CardDescription>
                <CardTitle className="text-2xl font-extrabold">{item.value}</CardTitle>
              </div>
              <div className={`p-3 rounded-xl ${item.color}`}>{item.icon}</div>
            </CardHeader>
          </Card>
        ))}
      </div>

      <Card className="rounded-2xl border-border/50 p-6">
        <CardHeader className="p-0 mb-6">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            营收趋势分析
          </CardTitle>
        </CardHeader>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={stats?.revenueData}>
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--muted))" />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
              <Tooltip 
                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
              />
              <Area type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={3} fillOpacity={1} fill="url(#colorValue)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );

  const renderOrders = () => (
    <Card className="rounded-2xl border-border/50 overflow-hidden animate-fade-in">
      <CardHeader className="p-6 border-b border-border/50 flex flex-row items-center justify-between bg-primary/5">
        <div>
          <CardTitle className="text-lg font-bold">所有订单</CardTitle>
          <CardDescription>查看和管理系统内的所有交易记录</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="h-9 w-64 pl-10 rounded-lg" placeholder="搜索订单号..." />
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            className="h-9 gap-2"
            onClick={() => {}}
          >
            <Filter className="h-4 w-4" /> 筛选
          </Button>
        </div>
      </CardHeader>
      <Table>
        <TableHeader className="bg-muted/50">
          <TableRow>
            <TableHead className="font-bold">订单号</TableHead>
            <TableHead className="font-bold">用户ID</TableHead>
            <TableHead className="font-bold text-center">金额</TableHead>
            <TableHead className="font-bold text-center">状态</TableHead>
            <TableHead className="font-bold text-center">下单时间</TableHead>
            <TableHead className="font-bold text-right">操作</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((order) => (
            <TableRow key={order.id} className="hover:bg-muted/30">
              <TableCell className="font-medium text-xs">{order.order_no}</TableCell>
              <TableCell className="text-xs truncate max-w-[120px]">{order.user_id}</TableCell>
              <TableCell className="text-center font-bold">¥{order.total_amount}</TableCell>
              <TableCell className="text-center">
                <Badge className={order.status === 'paid' ? 'bg-success text-white' : 'bg-muted text-muted-foreground'}>
                  {order.status === 'paid' ? '已支付' : '待处理'}
                </Badge>
              </TableCell>
              <TableCell className="text-center text-xs text-muted-foreground">
                {new Date(order.created_at).toLocaleString()}
              </TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => navigate(`/orders/${order.order_no}`)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );

  const renderCommunity = () => (
    <Card className="rounded-2xl border-border/50 overflow-hidden animate-fade-in">
      <CardHeader className="p-6 border-b border-border/50 flex flex-row items-center justify-between bg-secondary/5">
        <div>
          <CardTitle className="text-lg font-bold">内容审核</CardTitle>
          <CardDescription>管理社区内的所有帖子和讨论内容</CardDescription>
        </div>
        <Button 
          size="sm" 
          className="h-9 gap-2"
          onClick={() => { toast.success('已批量审核通过'); }}
        >
          <CheckCircle2 className="h-4 w-4" /> 批量通过
        </Button>
      </CardHeader>
      <Table>
        <TableHeader className="bg-muted/50">
          <TableRow>
            <TableHead className="font-bold">标题</TableHead>
            <TableHead className="font-bold">作者</TableHead>
            <TableHead className="font-bold text-center">点赞/评论</TableHead>
            <TableHead className="font-bold text-center">发布时间</TableHead>
            <TableHead className="font-bold text-right">操作</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {posts.map((post) => (
            <TableRow key={post.id} className="hover:bg-muted/30">
              <TableCell className="max-w-xs">
                <div className="font-bold line-clamp-1">{post.title}</div>
                <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{post.content}</div>
              </TableCell>
              <TableCell className="text-sm font-medium">{post.profiles?.username || '未知'}</TableCell>
              <TableCell className="text-center">
                <div className="flex items-center justify-center gap-3 text-xs font-bold">
                  <span className="flex items-center gap-1"><TrendingUp className="h-3 w-3 text-primary" />{post.likes_count}</span>
                  <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3 text-secondary" />{post.comments_count}</span>
                </div>
              </TableCell>
              <TableCell className="text-center text-xs text-muted-foreground">
                {new Date(post.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => navigate(`/community/post/${post.id}`)}>
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 border-red-100" onClick={() => handleDeletePost(post.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background pb-20 flex">
      {/* 侧边导航 */}
      <div className="w-64 border-r border-border/50 bg-background/50 backdrop-blur-md flex flex-col pt-24 px-4 space-y-2 sticky top-0 h-screen hidden lg:flex">
        <div className="px-4 mb-6">
          <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">控制中心</h2>
        </div>
        {[
          { id: 'overview', label: '总览看板', icon: <BarChart4 className="h-4 w-4" /> },
          { id: 'orders', label: '订单管理', icon: <ShoppingBag className="h-4 w-4" /> },
          { id: 'community', label: '内容审核', icon: <MessageSquare className="h-4 w-4" /> },
          { id: 'users', label: '用户管理', icon: <Users className="h-4 w-4" /> },
          { id: 'ai-stats', label: 'AI 成本统计', icon: <Zap className="h-4 w-4" /> },
          { id: 'config', label: '系统配置', icon: <Settings className="h-4 w-4" /> }
        ].map((item) => (
          <Button 
            key={item.id}
            variant={activeTab === item.id ? 'secondary' : 'ghost'} 
            className={`w-full justify-start gap-3 h-11 px-4 rounded-xl font-bold transition-all ${activeTab === item.id ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:bg-muted'}`}
            onClick={() => setActiveTab(item.id as any)}
          >
            {item.icon}
            {item.label}
          </Button>
        ))}
        <div className="flex-1" />
        <Button variant="outline" className="w-full mb-8 h-11 rounded-xl border-border gap-2 font-bold" onClick={() => navigate('/')}>
          <ArrowLeft className="h-4 w-4" />
          返回前台
        </Button>
      </div>

      {/* 主内容区 */}
      <div className="flex-1 p-6 md:p-10 pt-24 max-w-7xl mx-auto w-full">
        <div className="flex items-center justify-between mb-8">
          <div className="space-y-1">
            <h1 className="text-3xl font-extrabold flex items-center gap-3">
              <ShieldCheck className="h-8 w-8 text-primary" />
              管理中心
            </h1>
            <p className="text-muted-foreground font-medium">欢迎回来，管理员。在这里掌控万象生态的每一个环节。</p>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              className="rounded-xl border-border font-bold"
              onClick={() => { fetchStats(); toast.success('数据已同步'); }}
            >
              同步数据
            </Button>
            <Button 
              className="rounded-xl bg-primary text-white font-bold shadow-lg"
              onClick={() => { toast.info('报表正在生成中...'); }}
            >
              导出报表
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 space-y-4">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-sm font-bold text-muted-foreground">正在同步云端数据...</p>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'orders' && renderOrders()}
            {activeTab === 'community' && renderCommunity()}
            {activeTab === 'users' && renderUsers()}
            {activeTab === 'config' && renderConfig()}
            {activeTab === 'ai-stats' && renderAiStats()}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
